// The instructor's Requests tab: time requests students sent, newest first.
//
// Each open request offers two answers. "Publish a session" hands the request to the dashboard,
// which opens the session form prefilled from it and answers the request once the session is
// saved. "Cannot be offered" answers it here. The student gets one email either way.
import React from 'react';
import { api } from '../lib/api';
import { utcToZonedParts, zonedDateTimeToUtc, formatInTz } from '../lib/timezone';
import { WEEKDAYS, TIME_OF_DAY } from '../lib/sessionTime';
import { Badge, Btn } from './index';
import { palette as T, alpha, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Mirror TIME_REQUEST_STATUS in backend/src/lib/constants.js.
export const TIME_REQUEST_STATUS = { OPEN: 'open', PUBLISHED: 'published', DECLINED: 'declined' };
const STATUS_BADGE = {
  [TIME_REQUEST_STATUS.OPEN]: { label: 'Waiting for your answer', variant: 'gold' },
  [TIME_REQUEST_STATUS.PUBLISHED]: { label: 'Session published', variant: 'sage' },
  [TIME_REQUEST_STATUS.DECLINED]: { label: 'Cannot be offered', variant: 'subtle' },
};

const DAY_LABELS = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
// Same words as the email the instructor got (backend timeRequestEmails.js).
const TIME_OF_DAY_LABELS = {
  [TIME_OF_DAY.MORNING]: 'morning (before 12:00)',
  [TIME_OF_DAY.AFTERNOON]: 'afternoon (12:00 to 17:00)',
  [TIME_OF_DAY.EVENING]: 'evening (from 17:00)',
};
const TIME_OF_DAY_ORDER = Object.values(TIME_OF_DAY);
// Where the prefilled session starts inside each time of day, on the student's clock.
const TIME_OF_DAY_START = {
  [TIME_OF_DAY.MORNING]: '09:00',
  [TIME_OF_DAY.AFTERNOON]: '14:00',
  [TIME_OF_DAY.EVENING]: '18:00',
};
const PREFILL_MINUTES = 60;
// utcToZonedParts numbers weekdays from Sunday (0); WEEKDAYS starts on Monday.
const DAY_BY_WEEKDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_MS = 24 * 60 * 60 * 1000;

export function formatRequestDays(days = []) {
  return WEEKDAYS.filter((d) => days.includes(d)).map((d) => DAY_LABELS[d]).join(', ');
}

export function formatRequestTimes(timesOfDay = []) {
  return TIME_OF_DAY_ORDER.filter((t) => timesOfDay.includes(t)).map((t) => TIME_OF_DAY_LABELS[t]).join(', ');
}

const pad = (n) => String(n).padStart(2, '0');

// The session form fields for a request: the next requested day (never today) at the start of the
// earliest requested time of day, on the student's clock, projected into the instructor's timezone.
// -> { date: 'YYYY-MM-DD', time: 'HH:MM', endTime: 'HH:MM' } or null when nothing was requested.
export function requestToSessionSlot(request, viewerTz, now = new Date()) {
  const studentTz = request.timezone;
  const timeOfDay = TIME_OF_DAY_ORDER.find((t) => (request.timesOfDay || []).includes(t));
  if (!timeOfDay || !(request.days || []).length) return null;

  const today = utcToZonedParts(now, studentTz);
  for (let i = 1; i <= 7; i += 1) {
    // Calendar arithmetic on a UTC date: no DST or zone math, only the day and the weekday.
    const day = new Date(Date.UTC(today.year, today.month - 1, today.day) + i * DAY_MS);
    if (!request.days.includes(DAY_BY_WEEKDAY[day.getUTCDay()])) continue;
    const dateStr = `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}`;
    const start = zonedDateTimeToUtc(dateStr, TIME_OF_DAY_START[timeOfDay], studentTz);
    const p = utcToZonedParts(start, viewerTz);
    const end = utcToZonedParts(new Date(start.getTime() + PREFILL_MINUTES * 60 * 1000), viewerTz);
    return {
      date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
      time: `${pad(p.hour)}:${pad(p.minute)}`,
      endTime: `${pad(end.hour)}:${pad(end.minute)}`,
    };
  }
  return null;
}

// Shown when a session was saved for a time request but the answer did not go through. It keeps
// the saved session id, so Try again re-sends only the answer and never creates a second session.
function AnswerRetryBanner({ failed, busy, onRetry, onClose }) {
  return (
    <div role="alert" data-testid="answer-retry" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '12px 16px', marginBottom: 16 }}>
      <p style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: 0, fontWeight: 600 }}>
        Your session is scheduled, but we could not tell {failed.fromRequest.studentName} about it yet.
      </p>
      {failed.message && (
        <p style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: '4px 0 0' }}>{failed.message}</p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <Btn size="sm" onClick={onRetry} disabled={busy}>{busy ? 'Sending…' : 'Try again'}</Btn>
        <Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Close</Btn>
      </div>
    </div>
  );
}

// After the dashboard saves a session for a time request, answer the request with that session.
// -> { answer(fromRequest, sessionId), banner }: render `banner` where the instructor will see it.
// A 409 means the request was answered already, so the instructor is told that and nothing else.
// Any other failure keeps { fromRequest, sessionId } for Try again.
export function usePublishedAnswer(showSnackbar) {
  const [failed, setFailed] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const answer = React.useCallback(async (fromRequest, sessionId) => {
    setBusy(true);
    try {
      await api.post(`/time-requests/${fromRequest.id}/answer`, { outcome: TIME_REQUEST_STATUS.PUBLISHED, sessionId });
      setFailed(null);
      showSnackbar(`Session scheduled. We emailed ${fromRequest.studentName} a link to book it.`, { variant: 'success' });
    } catch (err) {
      if (err.status === 409) {
        setFailed(null);
        showSnackbar(`Session scheduled. ${fromRequest.studentName}'s time request was already answered, so we sent no new email.`, { variant: 'info' });
      } else {
        setFailed({ fromRequest, sessionId, message: err.data?.message || null });
      }
    } finally {
      setBusy(false);
    }
  }, [showSnackbar]);

  const banner = failed ? (
    <AnswerRetryBanner
      failed={failed}
      busy={busy}
      onRetry={() => answer(failed.fromRequest, failed.sessionId)}
      onClose={() => setFailed(null)}
    />
  ) : null;

  return { answer, banner };
}

const labelText = { fontFamily: FONT, fontSize: type.label, color: T.driftwood };
const valueText = { fontFamily: FONT, fontSize: type.body, color: T.espresso, fontWeight: 600 };

function RequestRow({ request, viewerTz, busy, onPublish, onDecline }) {
  const badge = STATUS_BADGE[request.status] || STATUS_BADGE[TIME_REQUEST_STATUS.OPEN];
  const open = request.status === TIME_REQUEST_STATUS.OPEN;
  return (
    <li style={{ listStyle: 'none', background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <p style={{ fontFamily: FONT, fontWeight: 700, fontSize: 15, color: T.espresso, margin: 0 }}>{request.studentName}</p>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <span style={{ ...labelText, marginLeft: 'auto' }}>Sent {formatInTz(request.createdAt, viewerTz, { month: 'short', day: 'numeric' })}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4 }}>
        <span style={labelText}>Days</span><span style={valueText}>{formatRequestDays(request.days)}</span>
        <span style={labelText}>Time of day</span><span style={valueText}>{formatRequestTimes(request.timesOfDay)}</span>
        <span style={labelText}>Their timezone</span><span style={valueText}>{request.timezone}</span>
      </div>
      {request.note && (
        <p style={{ fontFamily: FONT, fontSize: type.body, color: T.walnut, margin: '10px 0 0', fontStyle: 'italic', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          Note: “{request.note}”
        </p>
      )}
      {open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          <Btn size="sm" onClick={() => onPublish(request)} disabled={busy}>Publish a session</Btn>
          <Btn size="sm" variant="ghost" onClick={() => onDecline(request)} disabled={busy}>Cannot be offered</Btn>
        </div>
      )}
    </li>
  );
}

export function TimeRequestsPanel({ viewerTz, onPublish }) {
  const [requests, setRequests] = React.useState(null);
  const [loadError, setLoadError] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const [answerError, setAnswerError] = React.useState(null);

  React.useEffect(() => {
    let live = true;
    api.get('/time-requests/mine')
      .then((d) => { if (live) setRequests(d?.timeRequests || []); })
      .catch(() => { if (live) { setLoadError(true); setRequests([]); } });
    return () => { live = false; };
  }, []);

  const handleDecline = async (request) => {
    const ok = window.confirm(`Tell ${request.studentName} you cannot offer this time? They get one email pointing to Notify me on your profile.`);
    if (!ok) return;
    setAnswerError(null);
    setBusyId(request.id);
    try {
      const d = await api.post(`/time-requests/${request.id}/answer`, { outcome: TIME_REQUEST_STATUS.DECLINED });
      setRequests((list) => list.map((r) => (r.id === request.id ? { ...r, ...d.timeRequest } : r)));
    } catch (err) {
      setAnswerError(err.data?.message || 'We could not save your answer. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 8px' }}>Requests</h2>
      <p style={{ fontFamily: FONT, fontSize: type.body, color: T.walnut, margin: '0 0 20px' }}>
        Students ask for a time when you have no open seat. Publish a session for them, or tell them you cannot offer it. Days and times are on the student’s clock.
      </p>
      {answerError && (
        <div role="alert" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '10px 14px', marginBottom: 14 }}>
          <p style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: 0 }}>{answerError}</p>
        </div>
      )}
      {requests === null ? (
        <p style={{ fontFamily: FONT, fontSize: type.body, color: T.driftwood }}>Loading time requests…</p>
      ) : loadError ? (
        <p style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed }}>We could not load your time requests. Reload the page to try again.</p>
      ) : requests.length === 0 ? (
        <div style={{ background: T.white, borderRadius: 16, border: '1px dashed ' + T.border, padding: '32px 20px', textAlign: 'center' }}>
          <p style={{ fontFamily: FONT, fontSize: type.body, color: T.driftwood, margin: 0 }}>
            No time requests yet. When a student asks for a time you have no session at, it shows here.
          </p>
        </div>
      ) : (
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0, margin: 0 }}>
          {requests.map((r) => (
            <RequestRow key={r.id} request={r} viewerTz={viewerTz} busy={busyId === r.id} onPublish={onPublish} onDecline={handleDecline} />
          ))}
        </ul>
      )}
    </div>
  );
}
